import React, { useState, useEffect, useCallback } from 'react';
import { fetchAllQueries, deleteQuery } from '../api';
import DarkInput from '../components/DarkInput';
import GradientButton from '../components/GradientButton';
import Spinner from '../components/Spinner';
import QueryTableRow from '../components/QueryTableRow';
import { LoadingState, ErrorState, EmptyState } from '../components/QueryTableStates';
import CreateQueryModal from '../components/CreateQueryModal';
import ConfirmModal from '../components/ConfirmModal';
import ErrorBanner from '../components/ErrorBanner';

const TABLE_HEADERS = ['Category', 'Query Name', 'Premium', ''];

export default function QueriesList({ onSelect }) {
  const [queries, setQueries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null); // { id, name }
  const [deleteError, setDeleteError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try { setQueries(await fetchAllQueries()); }
    catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = queries.filter(q => {
    const t = search.toLowerCase();
    return q.query_name.toLowerCase().includes(t)
      || q.category.toLowerCase().includes(t)
      || (q.subcategory || '').toLowerCase().includes(t);
  });

  async function handleDeleteConfirmed() {
    const { id } = confirmDelete;
    setConfirmDelete(null);
    setDeletingId(id);
    setDeleteError(null);
    try {
      await deleteQuery(id);
      setQueries(prev => prev.filter(q => q.id !== id));
    } catch (err) {
      setDeleteError(err.message);
      setTimeout(() => setDeleteError(null), 3000);
    } finally {
      setDeletingId(null);
    }
  }

  return (
    <div className="p-8" style={{ minHeight: '100vh' }}>

      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-100 tracking-tight">Prebuilt Queries</h1>
          <p className="text-sm mt-1" style={{ color: '#6b7280' }}>
            {queries.length} {queries.length === 1 ? 'query' : 'queries'} stored
          </p>
        </div>
        <GradientButton
          onClick={() => setShowModal(true)}
          style={{ boxShadow: '0 4px 15px rgba(99,102,241,0.3)', padding: '10px 16px' }}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
          </svg>
          New Query
        </GradientButton>
      </div>

      {/* Search */}
      <div className="relative mb-5">
        <svg
          className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none"
          style={{ color: '#4b5563' }}
          fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"
        >
          <circle cx="11" cy="11" r="8" /><path strokeLinecap="round" d="M21 21l-4.35-4.35" />
        </svg>
        <DarkInput
          type="text"
          placeholder="Search by query name, category or subcategory…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ paddingLeft: '38px', fontSize: '14px' }}
        />
      </div>

      {/* Table */}
      <div className="rounded-2xl overflow-hidden" style={{ backgroundColor: '#14161e', border: '1px solid #1e2130' }}>
        {loading ? <LoadingState /> : error ? <ErrorState message={error} /> : filtered.length === 0 ? (
          <EmptyState search={search} />
        ) : (
          <table className="w-full">
            <thead>
              <tr style={{ borderBottom: '1px solid #1e2130', backgroundColor: '#0f1117' }}>
                {TABLE_HEADERS.map((h, i) => (
                  <th key={i} className="px-5 py-3.5 text-left"
                    style={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#4b5563' }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(q => (
                <QueryTableRow
                  key={q.id} query={q}
                  onView={() => onSelect(q.id)}
                  onDelete={() => setConfirmDelete({ id: q.id, name: q.query_name })}
                  deleting={deletingId === q.id}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showModal && (
        <CreateQueryModal onClose={() => setShowModal(false)} onCreated={load} />
      )}

      {confirmDelete && (
        <ConfirmModal
          title="Delete Query"
          message={`Delete "${confirmDelete.name}" and all its versions? This cannot be undone.`}
          onConfirm={handleDeleteConfirmed}
          onClose={() => setConfirmDelete(null)}
        />
      )}

      {deleteError && (
        <div className="fixed bottom-6 right-6 z-50 w-80">
          <ErrorBanner message={deleteError} />
        </div>
      )}
    </div>
  );
}
